<script setup>
import moment from 'moment';
import { DatePicker as VueDatePicker } from 'v-calendar';
import { computed, ref, watch } from 'vue';

// The value is a wall-clock "YYYY-MM-DD HH:mm" string. It is shown in one fixed format whatever the browser locale,
// and only local date fields are read or written, so the browser's timezone never shifts it.
const MODEL_FORMAT = 'YYYY-MM-DD HH:mm';
const DISPLAY_FORMAT = 'MM/DD/YYYY hh:mm A';

const emits = defineEmits(['update:modelValue']);

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  label: String,
  placeholder: {
    type: String,
    default: 'mm/dd/yyyy hh:mm AM',
  },
  invalidMessage: {
    type: String,
    default: '',
  },
  errorMessages: {
    type: /** @type {import('vue').PropType<string | string[]>} */ ([String, Array]),
    default: () => [],
  },
  disabled: Boolean,
});

/**
 * Model value to display text.
 * @param {string} value Model value.
 * @returns {string}
 */
function toDisplay(value) {
  const parsed = moment(value, MODEL_FORMAT, true);
  return parsed.isValid() ? parsed.format(DISPLAY_FORMAT) : '';
}

/**
 * Parse display text strictly.
 * @param {string} text Display text.
 * @returns {moment.Moment}
 */
function parseDisplay(text) {
  return moment(text, DISPLAY_FORMAT, true);
}

const inputValue = ref(toDisplay(props.modelValue));
const isInvalid = ref(false);

const pickerValue = computed(() => {
  const parsed = moment(props.modelValue, MODEL_FORMAT, true);
  return parsed.isValid() ? parsed.toDate() : null;
});

const shownErrors = computed(() => (isInvalid.value ? props.invalidMessage : props.errorMessages));

// Literal types (not plain strings) so the options match v-calendar's PopoverOptions.
const popover = /** @type {const} */ ({
  visibility: 'click',
  placement: 'bottom-start',
});

// Follow values set by the parent (load, save), but leave half-typed text alone.
watch(() => props.modelValue, value => {
  const typed = parseDisplay(inputValue.value);
  if (typed.isValid() && typed.format(MODEL_FORMAT) === value) {
    return;
  }
  if (value || typed.isValid()) {
    inputValue.value = toDisplay(value);
  }
});

/**
 * Handle typing: emit the value once the text is a full valid date and time, otherwise an empty value.
 * @param {string} text Display text.
 */
function onInputChange(text) {
  isInvalid.value = false;
  const parsed = parseDisplay(text);
  emits('update:modelValue', text && parsed.isValid() ? parsed.format(MODEL_FORMAT) : '');
}

/** Flag text that is not a full valid date and time. */
function onInputBlur() {
  isInvalid.value = Boolean(inputValue.value) && !parseDisplay(inputValue.value).isValid();
}

/**
 * Handle a date and time picked in the calendar.
 * @param {Date | null} date Picked date.
 */
function onPick(date) {
  isInvalid.value = false;
  if (!date) {
    inputValue.value = '';
    emits('update:modelValue', '');
    return;
  }
  inputValue.value = moment(date).format(DISPLAY_FORMAT);
  emits('update:modelValue', moment(date).format(MODEL_FORMAT));
}
</script>

<template>
  <vue-date-picker
    :model-value="pickerValue"
    mode="dateTime"
    locale="en-US"
    :popover="popover"
    @update:model-value="onPick"
  >
    <template #default="{ togglePopover }">
      <v-text-field
        v-model="inputValue"
        v-mask="'##/##/#### ##:## AA'"
        :label="label"
        :placeholder="placeholder"
        :disabled="disabled"
        :error-messages="shownErrors"
        variant="outlined"
        density="compact"
        autocomplete="off"
        @update:model-value="onInputChange"
        @blur="onInputBlur"
        @click="togglePopover"
      >
        <template #append-inner>
          <v-icon size="small" icon="mdi:mdi-calendar" />
        </template>
      </v-text-field>
    </template>
  </vue-date-picker>
</template>
